import { SpinnerButton, TextInput } from '@do/walrus';
import { Form, Formik } from 'formik';
import React, { ReactElement, useState } from 'react';
import * as Yup from 'yup';
import { EMAIL_MAX_LEN } from '../constants';
import copy from '../copy';
import { Variant, VariationTemplates } from '../interfaces/variant';
import { handlePlainFetchError } from '../utils/gql-utils';
import { yupPasswordValidation } from '../utils/validation-utils';
import { ErrorBanner } from './Common';
import NewErrorBanner from './newBrandDesign/ErrorBanner';
import NewSpinnerButton from './newBrandDesign/SpinnerButton';
import NewTextInput from './newBrandDesign/TextInput';

const formCopy = copy.form;

const FormValidation = Yup.object().shape({
  email: Yup.string()
    .email(formCopy.validation.email_valid)
    .required(formCopy.validation.email_required)
    .max(EMAIL_MAX_LEN, copy.errors.too_long.details),
  password: yupPasswordValidation
});

interface Props {
  email?: string;
  onboardingOrigin?: string;
  sessionId?: string;
  isLoadingOauthPath?: boolean;
  variant: Variant;
  isNewBrandDesignEnabled?: boolean;
}

const RegistrationForm: React.FC<Props> = ({
  email,
  onboardingOrigin,
  isLoadingOauthPath,
  variant,
  isNewBrandDesignEnabled
}): ReactElement => {
  const [genericServerError, setGenericServerError] = useState(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const isNewBrand =
    isNewBrandDesignEnabled &&
    variant?.version === VariationTemplates.NEW_BRAND;
  const SpinnerButtonComponent = isNewBrand ? NewSpinnerButton : SpinnerButton;
  const TextInputComponent = isNewBrand ? NewTextInput : TextInput;
  const ErrorBannerComponent = isNewBrand ? NewErrorBanner : ErrorBanner;

  return (
    <Formik
      initialValues={{
        name: '',
        password: '',
        email: email || '',
        onboarding_origin: onboardingOrigin || ''
      }}
      validationSchema={FormValidation}
      onSubmit={async (values, { setErrors }) => {
        setIsCreatingAccount(true);
        setGenericServerError(null); // Reset displayed error

        const response = await fetch(
          `${window.location.origin}/api/v1/registrations/email`,
          {
            method: 'POST',
            headers: new Headers([['Content-Type', 'application/json']]),
            body: JSON.stringify(values),
            credentials: 'same-origin'
          }
        );

        if (!response.ok) {
          handlePlainFetchError(setErrors, setGenericServerError)(response);
        } else if (response.redirected) {
          window.location.assign(response.url);
        }

        setIsCreatingAccount(false);
      }}
    >
      {({ errors, values, touched }) => (
        <Form autoComplete="none">
          {genericServerError && (
            <ErrorBannerComponent error={genericServerError} />
          )}

          <TextInputComponent
            placeholder={formCopy.name_placeholder}
            name="name"
            label={formCopy.name_label}
            className={isNewBrand ? '' : 'do-u-mb--small'}
            touched={touched.name}
            error={errors.name}
            value={values.name}
            autoComplete="none"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />

          <TextInputComponent
            placeholder={formCopy.email_placeholder}
            required
            name="email"
            label={formCopy.email_label}
            className={isNewBrand ? '' : 'do-u-mb--small'}
            touched={touched.email}
            error={errors.email}
            value={values.email}
            autoComplete="none"
          />

          <TextInputComponent
            placeholder={formCopy.register.password_placeholder}
            required
            name="password"
            label={formCopy.password_label}
            type="password"
            className={isNewBrand ? '' : 'do-u-mb--medium'}
            touched={touched.password}
            error={errors.password}
            value={values.password}
            autoComplete="none"
          />

          <SpinnerButtonComponent
            type="submit"
            variation="success"
            fullWidth={true}
            disabled={isCreatingAccount || isLoadingOauthPath}
            spinning={isCreatingAccount || isLoadingOauthPath}
            loadingMessage={copy.loading.registration.belowButton}
          >
            {formCopy.register.button_text}
          </SpinnerButtonComponent>
        </Form>
      )}
    </Formik>
  );
};

export default RegistrationForm;
